import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AdminLayout } from './AdminLayout';
import { articlesAPI, categoriesAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { toast } from 'sonner';
import { Plus, Search, Edit, Trash2, Eye, Star } from 'lucide-react';
import { formatDateShort } from '../../lib/utils';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../../components/ui/alert-dialog';

const statusColors = {
  draft: 'bg-yellow-100 text-yellow-800',
  in_review: 'bg-blue-100 text-blue-800',
  scheduled: 'bg-purple-100 text-purple-800',
  published: 'bg-green-100 text-green-800',
};

export const AdminArticles = () => {
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ status: '', search: '' });
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, [filter.status]);

  const fetchData = async () => {
    try {
      const [articlesRes, catsRes] = await Promise.all([
        articlesAPI.getAll({ status: filter.status || undefined }),
        categoriesAPI.getAll(),
      ]);
      setArticles(articlesRes.data.articles);
      setCategories(catsRes.data);
    } catch (error) {
      toast.error('Failed to load articles');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await articlesAPI.delete(id);
      toast.success('Article deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete article');
    }
  };

  const handleSetHeadline = async (id) => {
    try {
      await articlesAPI.setHeadline(id);
      toast.success('Article set as headline story');
      fetchData();
    } catch (error) {
      toast.error('Failed to set headline');
    }
  };

  const getCategoryName = (categoryId) => {
    const cat = categories.find(c => c.id === categoryId);
    return cat?.name || 'Unknown';
  };

  const filteredArticles = articles.filter(article => {
    if (filter.search) {
      return article.title.toLowerCase().includes(filter.search.toLowerCase());
    }
    return true;
  });

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-articles-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="font-serif text-2xl md:text-3xl font-bold">Articles</h1>
            <p className="text-muted-foreground">Manage your articles and content</p>
          </div>
          <Link to="/staff/articles/new">
            <Button data-testid="new-article-btn">
              <Plus className="h-4 w-4 mr-2" />
              New Article
            </Button>
          </Link>
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search articles..."
              value={filter.search}
              onChange={(e) => setFilter(prev => ({ ...prev, search: e.target.value }))}
              className="pl-9"
              data-testid="search-articles"
            />
          </div>
          <Select
            value={filter.status}
            onValueChange={(value) => setFilter(prev => ({ ...prev, status: value }))}
          >
            <SelectTrigger className="w-[180px]" data-testid="filter-status">
              <SelectValue placeholder="All Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="in_review">In Review</SelectItem>
              <SelectItem value="scheduled">Scheduled</SelectItem>
              <SelectItem value="published">Published</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Table */}
        <div className="border border-border rounded-sm overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Title</TableHead>
                <TableHead className="hidden md:table-cell">Category</TableHead>
                <TableHead className="hidden md:table-cell">Author</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="hidden md:table-cell">Date</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell><Skeleton className="h-4 w-48" /></TableCell>
                    <TableCell className="hidden md:table-cell"><Skeleton className="h-4 w-24" /></TableCell>
                    <TableCell className="hidden md:table-cell"><Skeleton className="h-4 w-24" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-20" /></TableCell>
                    <TableCell className="hidden md:table-cell"><Skeleton className="h-4 w-24" /></TableCell>
                    <TableCell><Skeleton className="h-8 w-24 ml-auto" /></TableCell>
                  </TableRow>
                ))
              ) : filteredArticles.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-12 text-muted-foreground">
                    No articles found
                  </TableCell>
                </TableRow>
              ) : (
                filteredArticles.map((article) => (
                  <TableRow key={article.id} data-testid={`article-row-${article.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {article.is_headline_featured && (
                          <Star className="h-4 w-4 text-primary fill-primary flex-shrink-0" />
                        )}
                        <div className="font-medium line-clamp-1">{article.title}</div>
                      </div>
                      <div className="text-sm text-muted-foreground md:hidden">
                        {getCategoryName(article.category_id)}
                      </div>
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      {getCategoryName(article.category_id)}
                    </TableCell>
                    <TableCell className="hidden md:table-cell">{article.author}</TableCell>
                    <TableCell>
                      <Badge className={statusColors[article.status]}>
                        {article.status.replace('_', ' ')}
                      </Badge>
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      {formatDateShort(article.created_at)}
                    </TableCell>
                    <TableCell>
                      <div className="flex justify-end gap-1">
                        {article.status === 'published' && !article.is_headline_featured && (
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => handleSetHeadline(article.id)}
                            title="Set as Headline Story"
                            data-testid={`headline-${article.id}`}
                          >
                            <Star className="h-4 w-4 text-muted-foreground hover:text-primary" />
                          </Button>
                        )}
                        {article.status === 'published' && (
                          <Link to={`/article/${article.slug}`} target="_blank">
                            <Button variant="ghost" size="icon" data-testid={`view-${article.id}`}>
                              <Eye className="h-4 w-4" />
                            </Button>
                          </Link>
                        )}
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => navigate(`/staff/articles/${article.id}`)}
                          data-testid={`edit-${article.id}`}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="icon" data-testid={`delete-${article.id}`}>
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete Article</AlertDialogTitle>
                              <AlertDialogDescription>
                                Are you sure you want to delete "{article.title}"? This action cannot be undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction onClick={() => handleDelete(article.id)}>
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminArticles;
